import { z } from "zod"

import {
  entityIdSchema,
  platformOperatorIdSchema,
  tenantIdSchema,
} from "./identifiers.js"
import { platformCommandEnvelopeV1Schema } from "./platform-command-v1.js"

const timestampSchema = z.iso.datetime({ offset: true })
const codeSchema = z.string().regex(/^[a-z][a-z0-9_.-]{1,63}$/)
const evidenceReferenceSchema = z.string().regex(/^evidence:\/\/sha256\/[0-9a-f]{64}$/)
const safeTextSchema = z.string().min(1).max(500)

export const platformIncidentStateV1Schema = z.enum([
  "declared",
  "investigating",
  "contained",
  "monitoring",
  "resolved",
  "closed",
])

export const platformIncidentTimelineEntryV1Schema = z.object({
  timelineEntryId: entityIdSchema,
  kind: z.enum([
    "state_change",
    "containment",
    "communication",
    "evidence_link",
    "postmortem_action",
  ]),
  summary: safeTextSchema,
  evidenceReference: evidenceReferenceSchema.nullable(),
  platformCommunicationId: entityIdSchema.nullable(),
  attributedPlatformOperatorId: platformOperatorIdSchema,
  occurredAt: timestampSchema,
}).strict()

export const platformIncidentV1Schema = z.object({
  contractVersion: z.literal(1),
  incidentId: entityIdSchema,
  referenceCode: z.string().regex(/^INC-[0-9]{6}$/),
  severity: z.enum(["sev1", "sev2", "sev3", "sev4"]),
  state: platformIncidentStateV1Schema,
  title: z.string().min(1).max(160),
  commanderPlatformOperatorId: platformOperatorIdSchema,
  affectedTenantIds: z.array(tenantIdSchema).max(200),
  timeline: z.array(platformIncidentTimelineEntryV1Schema).max(500),
  postmortemDueAt: timestampSchema.nullable(),
  version: z.number().int().positive(),
  declaredAt: timestampSchema,
  updatedAt: timestampSchema,
  safeMetadataOnly: z.literal(true),
}).strict().superRefine((incident, context) => {
  if (new Set(incident.affectedTenantIds).size !== incident.affectedTenantIds.length) {
    context.addIssue({ code: "custom", message: "Affected Tenants must be unique." })
  }
  if (incident.state === "closed" && incident.postmortemDueAt === null) {
    context.addIssue({ code: "custom", message: "Closed incident requires postmortem due date." })
  }
})

export const suspiciousAccessReviewV1Schema = z.object({
  contractVersion: z.literal(1),
  securityReviewId: entityIdSchema,
  state: z.enum(["open", "investigating", "escalated", "resolved", "dismissed"]),
  subjectType: z.enum([
    "platform_operator",
    "support_access_grant",
    "support_session",
    "platform_audit_export",
  ]),
  subjectId: entityIdSchema,
  targetTenantId: tenantIdSchema.nullable(),
  reasonCode: codeSchema,
  evidenceReferences: z.array(evidenceReferenceSchema).max(100),
  assignedPlatformOperatorId: platformOperatorIdSchema,
  resolutionCode: codeSchema.nullable(),
  version: z.number().int().positive(),
  openedAt: timestampSchema,
  updatedAt: timestampSchema,
  safeMetadataOnly: z.literal(true),
}).strict()

export const platformLegalHoldV1Schema = z.object({
  contractVersion: z.literal(1),
  legalHoldId: entityIdSchema,
  state: z.enum(["active", "released"]),
  subjectType: z.enum([
    "platform_incident",
    "platform_support_case",
    "platform_audit",
    "security_review",
    "tenant_offboarding",
  ]),
  subjectId: entityIdSchema,
  targetTenantIds: z.array(tenantIdSchema).max(200),
  reasonCode: codeSchema,
  retentionOverride: z.literal("indefinite_until_release"),
  appliedByPlatformOperatorId: platformOperatorIdSchema,
  releasedByPlatformOperatorId: platformOperatorIdSchema.nullable(),
  version: z.number().int().positive(),
  appliedAt: timestampSchema,
  releasedAt: timestampSchema.nullable(),
  safeMetadataOnly: z.literal(true),
}).strict().superRefine((hold, context) => {
  if (
    (hold.state === "active" && (hold.releasedAt !== null
      || hold.releasedByPlatformOperatorId !== null))
    || (hold.state === "released" && (hold.releasedAt === null
      || hold.releasedByPlatformOperatorId === null))
  ) {
    context.addIssue({ code: "custom", message: "Legal Hold release evidence is inconsistent." })
  }
})

const incidentRequestSchema = z.object({
  operation: z.literal("incident"),
  incidentId: entityIdSchema,
  action: z.enum([
    "declare",
    "start_investigation",
    "record_containment",
    "start_monitoring",
    "record_communication",
    "link_evidence",
    "resolve",
    "close",
    "add_postmortem_action",
  ]),
  referenceCode: z.string().regex(/^INC-[0-9]{6}$/).optional(),
  severity: z.enum(["sev1", "sev2", "sev3", "sev4"]).optional(),
  title: z.string().min(1).max(160).optional(),
  commanderPlatformOperatorId: platformOperatorIdSchema.optional(),
  affectedTenantIds: z.array(tenantIdSchema).max(200).optional(),
  timelineEntryId: entityIdSchema.optional(),
  summary: safeTextSchema.optional(),
  evidenceReference: evidenceReferenceSchema.nullable().optional(),
  platformCommunicationId: entityIdSchema.nullable().optional(),
  postmortemDueAt: timestampSchema.nullable().optional(),
}).strict()

const reviewRequestSchema = z.object({
  operation: z.literal("security_review"),
  securityReviewId: entityIdSchema,
  action: z.enum(["open", "start_investigation", "escalate", "add_evidence", "resolve", "dismiss"]),
  subjectType: z.enum([
    "platform_operator",
    "support_access_grant",
    "support_session",
    "platform_audit_export",
  ]).optional(),
  subjectId: entityIdSchema.optional(),
  targetTenantId: tenantIdSchema.nullable().optional(),
  reasonCode: codeSchema.optional(),
  evidenceReference: evidenceReferenceSchema.optional(),
  assignedPlatformOperatorId: platformOperatorIdSchema.optional(),
  resolutionCode: codeSchema.optional(),
}).strict()

const holdRequestSchema = z.object({
  operation: z.literal("legal_hold"),
  legalHoldId: entityIdSchema,
  action: z.enum(["apply", "release"]),
  subjectType: z.enum([
    "platform_incident",
    "platform_support_case",
    "platform_audit",
    "security_review",
    "tenant_offboarding",
  ]).optional(),
  subjectId: entityIdSchema.optional(),
  targetTenantIds: z.array(tenantIdSchema).max(200).optional(),
  reasonCode: codeSchema,
}).strict()

export const managePlatformSecurityOperationV1Schema = z.object({
  envelope: platformCommandEnvelopeV1Schema,
  request: z.discriminatedUnion("operation", [
    incidentRequestSchema,
    reviewRequestSchema,
    holdRequestSchema,
  ]),
}).strict().superRefine((command, context) => {
  const id = command.request.operation === "incident"
    ? command.request.incidentId
    : command.request.operation === "security_review"
      ? command.request.securityReviewId
      : command.request.legalHoldId
  if (
    command.envelope.target.scope !== "platform"
    || command.envelope.target.targetId !== id
    || command.envelope.target.targetType !== command.request.operation
  ) {
    context.addIssue({ code: "custom", message: "Security operation target mismatch." })
  }
  if (
    command.request.operation === "legal_hold"
    && command.envelope.requiredApprovalIds.length !== 1
  ) {
    context.addIssue({ code: "custom", message: "Legal Hold changes require one approval." })
  }
})

export type PlatformIncidentV1 = z.infer<typeof platformIncidentV1Schema>
export type SuspiciousAccessReviewV1 = z.infer<typeof suspiciousAccessReviewV1Schema>
export type PlatformLegalHoldV1 = z.infer<typeof platformLegalHoldV1Schema>
export type ManagePlatformSecurityOperationV1 = z.infer<
  typeof managePlatformSecurityOperationV1Schema
>
export type PlatformSecurityAggregateV1 =
  | PlatformIncidentV1
  | SuspiciousAccessReviewV1
  | PlatformLegalHoldV1
